import { type EasingName, type LocalTransform, type Rotation2, TRANSFORM_STRIDE } from "./types.js";

const EPSILON = 1e-12;

export function rotationFromRadians(angle: number): Rotation2 {
  return [Math.cos(angle), Math.sin(angle)];
}

export function rotationFromDegrees(angle: number): Rotation2 {
  return rotationFromRadians(angle * Math.PI / 180);
}

export function normalizeRotation(cos: number, sin: number): Rotation2 {
  const length = Math.hypot(cos, sin);
  return length < EPSILON ? [1, 0] : [cos / length, sin / length];
}

export function multiplyRotations(left: Rotation2, right: Rotation2): Rotation2 {
  return normalizeRotation(left[0] * right[0] - left[1] * right[1], left[1] * right[0] + left[0] * right[1]);
}

export function inverseRotation(rotation: Rotation2): Rotation2 {
  return [rotation[0], -rotation[1]];
}

export function interpolateRotation(from: Rotation2, to: Rotation2, amount: number): Rotation2 {
  const dot = from[0] * to[0] + from[1] * to[1];
  const cross = from[0] * to[1] - from[1] * to[0];
  const delta = Math.atan2(cross, dot);
  return multiplyRotations(from, rotationFromRadians(delta * amount));
}

export function ease(name: EasingName, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  if (name === "step") return t < 1 ? 0 : 1;
  if (name === "ease_in") return t * t;
  if (name === "ease_out") return 1 - (1 - t) * (1 - t);
  if (name === "ease_in_out") return t * t * (3 - 2 * t);
  return t;
}

export function writeTransform(buffer: ArrayLike<number> & { [index: number]: number }, index: number, transform: LocalTransform): void {
  const offset = index * TRANSFORM_STRIDE;
  buffer[offset] = transform.x;
  buffer[offset + 1] = transform.y;
  buffer[offset + 2] = transform.rotation[0];
  buffer[offset + 3] = transform.rotation[1];
  buffer[offset + 4] = transform.scaleX;
  buffer[offset + 5] = transform.scaleY;
}

export function readRotation(buffer: ArrayLike<number>, index: number): Rotation2 {
  const offset = index * TRANSFORM_STRIDE;
  return [buffer[offset + 2], buffer[offset + 3]];
}

export function blendPoseBuffers(from: ArrayLike<number>, to: ArrayLike<number>, amount: number, out: Float64Array): Float64Array {
  const alpha = Math.max(0, Math.min(1, amount));
  for (let offset = 0; offset < out.length; offset += TRANSFORM_STRIDE) {
    out[offset] = from[offset] + (to[offset] - from[offset]) * alpha;
    out[offset + 1] = from[offset + 1] + (to[offset + 1] - from[offset + 1]) * alpha;
    const rotation = interpolateRotation([from[offset + 2], from[offset + 3]], [to[offset + 2], to[offset + 3]], alpha);
    out[offset + 2] = rotation[0];
    out[offset + 3] = rotation[1];
    out[offset + 4] = from[offset + 4] + (to[offset + 4] - from[offset + 4]) * alpha;
    out[offset + 5] = from[offset + 5] + (to[offset + 5] - from[offset + 5]) * alpha;
  }
  return out;
}

export function applyAdditiveBone(base: Float64Array, layer: ArrayLike<number>, neutral: ArrayLike<number>, bone: number, weight: number): void {
  const offset = bone * TRANSFORM_STRIDE;
  base[offset] += (layer[offset] - neutral[offset]) * weight;
  base[offset + 1] += (layer[offset + 1] - neutral[offset + 1]) * weight;
  const delta = multiplyRotations(inverseRotation([neutral[offset + 2], neutral[offset + 3]]), [layer[offset + 2], layer[offset + 3]]);
  const weightedDelta = interpolateRotation([1, 0], delta, weight);
  const rotation = multiplyRotations([base[offset + 2], base[offset + 3]], weightedDelta);
  base[offset + 2] = rotation[0];
  base[offset + 3] = rotation[1];
  base[offset + 4] *= Math.pow(layer[offset + 4] / neutral[offset + 4], weight);
  base[offset + 5] *= Math.pow(layer[offset + 5] / neutral[offset + 5], weight);
}

export function applyOverrideBone(base: Float64Array, layer: ArrayLike<number>, bone: number, weight: number): void {
  const offset = bone * TRANSFORM_STRIDE;
  base[offset] += (layer[offset] - base[offset]) * weight;
  base[offset + 1] += (layer[offset + 1] - base[offset + 1]) * weight;
  const rotation = interpolateRotation([base[offset + 2], base[offset + 3]], [layer[offset + 2], layer[offset + 3]], weight);
  base[offset + 2] = rotation[0];
  base[offset + 3] = rotation[1];
  base[offset + 4] += (layer[offset + 4] - base[offset + 4]) * weight;
  base[offset + 5] += (layer[offset + 5] - base[offset + 5]) * weight;
}
